import { useState, useRef, useEffect, useCallback } from 'react';
// import { Order, OrderItem, SelectedBatch, SerialNumber } from '../types';

import { demoOrders } from '../data/demoData';
import { useSelectedArticleStore } from '../../../store/store';
import { AddItemToOutboundOrderRequest, Batch, OutboundOrder, OutboundOrderItem, Pageable, SelectedBatch, SerialNumber, WarehouseApi } from '../../../api/warehouse/generated/src';
import { getWarehouseApiConfig } from '../../../api-configs/WarehouseApiConfig';
import { IPaging } from '../../../types/interfaces';
import { findNextCyclic } from '../utils/arrayHelper';

interface IArticleAmount {
    articleId: string;
    batch?:string;
    serial?:string;
    value: number;
}

export const useWarehouseApp = () => {

    const defaultIsMinimum = true;

    // State Management
    // const [orders, setOrders] = useState<OutboundOrder[]>(demoOrders);
    const [orders, setOrders] = useState<OutboundOrder[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<OutboundOrder | null>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [scanType, setScanType] = useState<'order' | 'item'>('order');
    const [currentView, setCurrentView] = useState<'details' | 'orders' | 'picklist'>('picklist');

    // Modal states
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [showSerialModal, setShowSerialModal] = useState(false);
    const [showQtyModal, setShowQtyModal] = useState(false);
    const [showBatchQtyEditModal, setShowBatchQtyEditModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<OutboundOrderItem | null>(null);
    const [selectedBatchIndex, setSelectedBatchIndex] = useState<number | null>(null);

    // Input states
    const [serialInput, setSerialInput] = useState('');
    const [qtyInput, setQtyInput] = useState('');
    const [batchQtyInput, setBatchQtyInput] = useState('');
    const [batchQtyEditInput, setBatchQtyEditInput] = useState('');
    const [selectedBatchesTemp, setSelectedBatchesTemp] = useState<SelectedBatch[]>([]);

    // UI states
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());

    // Refs
    const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const hasInitialized = useRef(false);

    // Calculate progress
    const totalRequiredQty = selectedOrder?.items.reduce((sum, item) => sum + (item.requiredQty - item.shippedQty), 0) || 0;
    const totalPickedQty = selectedOrder?.items.reduce((sum, item) => sum + item.pickedQty, 0) || 0;
    const totalShippedQty = selectedOrder?.items.reduce((sum, item) => sum + item.shippedQty, 0) || 0;
    const progress = totalRequiredQty > 0 ? (totalPickedQty / totalRequiredQty) * 100 : 0;
    const allCompleted = selectedOrder?.items.every(i => i.status === 'completed') || false;
    const hasPickedItems = selectedOrder?.items.some(i => i.pickedQty > 0) || false;


    
    const [paging, setPaging] = useState<IPaging>({
        page: 1,
        limit: 10
    });

    const [meta, setMeta] = useState<Pageable>();

     const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    

        setPaging((oldPageing) => {

            return {
                ...oldPageing,
                page: page
            }
        })
    }

    useEffect(() => {
        const fetchData = async () => {

            const warehouseApi = new WarehouseApi(getWarehouseApiConfig());

            
            try {
                const outboundOrders = await warehouseApi.getOutboundOrders({
                    limit: paging.limit,
                    page: paging.page
                });
                


                const items = outboundOrders.items;





                if(items){
                    setOrders(items);
                    setMeta(outboundOrders.meta)
                }
                        
                
            } catch (error) {
                
            }    
        }

        fetchData();
    }, [paging.limit, paging.page]);
  


    // Initialize first item on order selection
    useEffect(() => {
        if (selectedOrder && !hasInitialized.current) {
            const firstPending = selectedOrder.items.find(item => item.status === 'pending');
            if (firstPending) {
                setTimeout(() => {
                    scrollToItem(firstPending.id);
                    activatePickToLight(firstPending.id);
                }, 100);
            }
            hasInitialized.current = true;
        }

        if (!selectedOrder) {
            hasInitialized.current = false;
        }
    }, [selectedOrder]);

    // Core Functions
    const scrollToItem = (itemId: string) => {
        const element = itemRefs.current[itemId];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const activatePickToLight = (itemId: string) => {
        // if (!selectedOrder) return;

        // const updatedItems = selectedOrder.items.map(item => ({
        //     ...item,
        //     pickToLightActive: item.id === itemId,
        //     status: item.id === itemId && item.status === 'pending' ? 'picking' as const : item.status
        // }));

        // setSelectedOrder({ ...selectedOrder, items: updatedItems });
    };

    const handleOrderSelect = (order: OutboundOrder | null) => {
        setSelectedOrder(order);
        if (order) {
            setCurrentView('picklist');
        }
    };

    const handleScan = async (barcode: string) => {
        if (scanType === 'order') {
            const order = orders.find(o => {

                const orderMatched = o.orderNumber === barcode;

                if(orderMatched){
                    return true;
                }

                const subOrderFound = o.subOrders?.find((subO) => {

                    return subO.orderNumber === barcode;
                });

                if(subOrderFound != null){
                    return true;
                }

                // o.barcode === barcode || o.orderNumber === barcode

                return false;

            });
            if (order) {
                handleOrderSelect(order);
                setIsScanning(false);
            }
        } else if (scanType === 'item' && selectedOrder) {

            const newOutboundOrder = await runOutboundSearch(barcode);


            if(newOutboundOrder){
                setSelectedOrder(newOutboundOrder);
            }

            // const item = selectedOrder.items.find(i => i.barcode === barcode || i.articleNumber === barcode);
            // if (item) {
            //     scrollToItem(item.id);
            //     setIsScanning(false);
            //     if (item.status === 'pending') {
            //         activatePickToLight(item.id);
            //     }
            // }


        }
    };

    const selectedArticle = useSelectedArticleStore(state => state.article);
    const setSelectedArticle = useSelectedArticleStore(state => state.updateSelected);

    const handleItemClick = (item: OutboundOrderItem) => {

        setSelectedArticle(item.id, "");

        scrollToItem(item.id);

        // if (item.status === 'pending') {
        //   activatePickToLight(item.id);
        // } else if (item.status === 'completed' || item.status === 'picking') {
        //   reopenItem(item.id);
        //   setTimeout(() => {
        //     activatePickToLight(item.id);
        //   }, 50);
        // }
    };

    const reopenItem = (itemId: string) => {
        if (!selectedOrder) return;

        const updatedItems = selectedOrder.items.map(item =>
            item.id === itemId
                ? {
                    ...item,
                    status: 'pending' as const,
                    pickedQty: 0,
                    pickToLightActive: false,
                    selectedBatches: [],
                    serialNumbers: []
                }
                : item
        );

        setSelectedOrder({ ...selectedOrder, items: updatedItems });
    };

    const toggleItemExpanded = (itemId: string) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(itemId)) {
            newExpanded.delete(itemId);
        } else {
            newExpanded.add(itemId);
        }
        setExpandedItems(newExpanded);
    };

    const toggleLocationExpanded = (itemId: string) => {
        const newExpanded = new Set(expandedLocations);
        if (newExpanded.has(itemId)) {
            newExpanded.delete(itemId);
        } else {
            newExpanded.add(itemId);
        }
        setExpandedLocations(newExpanded);
    };

    // Batch Management
    const openBatchSelection = (item: OutboundOrderItem) => {
        setSelectedItem(item);
        setSelectedBatchesTemp(item.selectedBatches || []);
        setBatchQtyInput('');
        setShowBatchModal(true);
    };

    const addBatchToSelection = async (batch: Batch, quantity: number) => {
        if (!selectedItem) return;

        const remainingQty = selectedItem.requiredQty - selectedItem.shippedQty;
        // const qty = parseInt(batchQtyInput) || Math.min(batch.availableQty, remainingQty - getTotalSelectedQty());
        // let qty = parseInt(batchQtyInput);

        let qty = quantity;
        if(isNaN(qty)){
            if(defaultIsMinimum){
                qty = 1;
            }
            else{
                qty = Math.min(batch.availableQty, remainingQty - getTotalSelectedQty())
            }
        }


        if (qty <= 0) return;

        const newOutboundOrder = await incrementArticleAmount({
            articleId: selectedItem.articleNumber,
            value: qty,
            batch: batch.batchNumber
        });

        if(newOutboundOrder){
            setSelectedOrder(newOutboundOrder);
        }

        const newSelection: SelectedBatch = { batch, quantity: qty };
        setSelectedBatchesTemp([...selectedBatchesTemp, newSelection]);
        setBatchQtyInput('');        
    };

    const removeBatchFromSelection = (batchId: string) => {
        setSelectedBatchesTemp(selectedBatchesTemp.filter(sb => sb.batch.id !== batchId));
    };

    const getTotalSelectedQty = () => {
        return selectedBatchesTemp.reduce((sum, sb) => sum + sb.quantity, 0);
    };

    const confirmBatchSelection = () => {
        if (!selectedOrder || !selectedItem || selectedBatchesTemp.length === 0) return;

        const totalQty = getTotalSelectedQty();
        // const updatedItems = selectedOrder.items.map(item =>
        //     item.id === selectedItem.id
        //         ? {
        //             ...item,
        //             selectedBatches: selectedBatchesTemp,
        //             pickedQty: totalQty,
        //             status: 'completed' as const,
        //             pickToLightActive: false
        //         }
        //         : item
        // );

        const updatedItems = selectedOrder.items;

        // setSelectedOrder({ ...selectedOrder, items: updatedItems });
        setShowBatchModal(false);
        setSelectedItem(null);
        setSelectedBatchesTemp([]);

        const currentIndex = updatedItems.findIndex((i) => i.id === selectedItem?.id); // aktuelles Element
        const nextItem = findNextCyclic(updatedItems, currentIndex, (i) => i.status === 'pending' && (i.requiredQty - i.pickedQty) > 0);

        // const nextItem = updatedItems.find(i => i.status === 'pending' && (i.requiredQty - i.pickedQty) > 0);

        if (nextItem) {
            setSelectedArticle(nextItem.id, "");
            setSelectedItem(nextItem);

            scrollToItem(nextItem.id);
            activatePickToLight(nextItem.id);
        }
    };

    // Serial Number Management
    const openSerialInput = (item: OutboundOrderItem) => {
        setSelectedItem(item);
        setShowSerialModal(true);
        setSerialInput('');
    };

    const addSerialNumber = async () => {
        if (!selectedOrder || !selectedItem || !serialInput.trim()) return;

        const newSerial: SerialNumber = {
            id: Date.now().toString(),
            number: serialInput.trim()
        };

        const currentSerials = selectedItem.serialNumbers || [];
        const updatedSerials = [...currentSerials, newSerial];
        const remainingQty = selectedItem.requiredQty - selectedItem.shippedQty;
        const isComplete = updatedSerials.length >= remainingQty;



        const newOutboundOrder = await addNewSerialOrBatch({
            articleId: selectedItem.articleNumber,
            serial: newSerial.number
        });

        if(newOutboundOrder){
            setSelectedOrder(newOutboundOrder);

            const newSelectedItem = newOutboundOrder?.items.find((nItem) => {
                return nItem.articleNumber === selectedItem.articleNumber;
            });

            if(newSelectedItem){
                setSelectedItem(newSelectedItem);
            }
        }
      

        const updatedItems = newOutboundOrder?.items;


        setSerialInput('');

        if (isComplete) {
            setShowSerialModal(false);
            setSelectedItem(null);

            const nextItem = updatedItems?.find(i => i.status === 'pending' && (i.requiredQty - i.pickedQty) > 0);
            if (nextItem) {
                setSelectedArticle(nextItem.id, "");
                setSelectedItem(nextItem);

                scrollToItem(nextItem.id);
                activatePickToLight(nextItem.id);
            }
        }
    };

    const removeSerialNumber = async (serialId: string) => {
        if (!selectedOrder || !selectedItem) return;

        let orderId = selectedOrder?.id;

        if (orderId) {
            const warehouseApi: WarehouseApi = new WarehouseApi(getWarehouseApiConfig());

            let outboundOrder: OutboundOrder | null = null;

            try {
                // const newSalesWrap = await salesOrderApi.incrementArticleAmount({id:orderId, });
                const tmpSalesWrap = await warehouseApi.removeSerialFromOutboundOrderItem({ 
                    orderId: orderId, 
                    itemId: selectedItem.articleNumber, 
                    removeSerialFromOutboundOrderItemRequest:{
                        serialNumber:serialId
                    } 
                });

                outboundOrder = tmpSalesWrap;


            } catch (error) {

            }


            if (outboundOrder) {
                // return outboundOrder;
                setSelectedOrder(outboundOrder);

                const newSelectedItem = outboundOrder?.items.find((nItem) => {
                    return nItem.articleNumber === selectedItem.articleNumber;
                });

                if(newSelectedItem){
                    setSelectedItem(newSelectedItem);
                }


            }


        }

        // const updatedSerials = (selectedItem.serialNumbers || []).filter(s => s.id !== serialId);

        // const updatedItems = selectedOrder.items.map(item =>
        //     item.id === selectedItem.articleNumber
        //         ? { ...item, serialNumbers: updatedSerials, pickedQty: updatedSerials.length, status: 'picking' as const }
        //         : item
        // );

        // setSelectedOrder({ ...selectedOrder, items: updatedItems });
        // setSelectedItem({ ...selectedItem, serialNumbers: updatedSerials });
    };

    const confirmPartialSerials = () => {
        if (!selectedOrder || !selectedItem) return;

        // const updatedItems = selectedOrder.items.map(i =>
        //     i.id === selectedItem.id
        //         ? { ...i, status: 'completed' as const, pickToLightActive: false }
        //         : i
        // );

        // setSelectedOrder({ ...selectedOrder, items: updatedItems });

        const updatedItems = selectedOrder.items;


        setShowSerialModal(false);
        setSelectedItem(null);

        const currentIndex = updatedItems.findIndex((i) => i.id === selectedItem?.id); // aktuelles Element
        const nextItem = findNextCyclic(updatedItems, currentIndex, (i) => i.status === 'pending' && (i.requiredQty - i.pickedQty) > 0);

        // const nextItem = updatedItems.find(i => i.status === 'pending' && (i.requiredQty - i.pickedQty) > 0);

        if (nextItem) {
            setSelectedArticle(nextItem.id, "");
            setSelectedItem(nextItem);

            scrollToItem(nextItem.id);
            activatePickToLight(nextItem.id);
        }
    };

    // Quantity Management
    const confirmStandardPick = async (item: OutboundOrderItem) => {
        if (!selectedOrder) return;

        const remainingQty = item.requiredQty - item.shippedQty;
        // const qty = parseInt(qtyInput) || remainingQty;
        const qty = defaultIsMinimum ? 1 : remainingQty;



        const newOutboundOrder = await incrementArticleAmount({
            articleId: item.articleNumber,
            value: qty
        });

        
        // const updatedItems = selectedOrder.items.map(i =>
        //     i.id === item.id
        //         ? { ...i, pickedQty: qty, status: 'completed' as const, pickToLightActive: false }
        //         : i
        // );

        // setSelectedOrder({ ...selectedOrder, items: updatedItems });

        let updatedItems = selectedOrder.items;

        if(newOutboundOrder){
            setSelectedOrder(newOutboundOrder);

            updatedItems = newOutboundOrder.items;
        }
        
        setQtyInput('');

        
        const currentIndex = updatedItems.findIndex((i) => i.id === item?.id); // aktuelles Element
        const nextItem = findNextCyclic(updatedItems, currentIndex, (i) => i.status === 'pending' && (i.requiredQty - i.pickedQty) > 0);

        // const nextItem = updatedItems.find(i => i.status === 'pending' && (i.requiredQty - i.pickedQty) > 0);

        if (nextItem) {
            setSelectedArticle(nextItem.id, "");
            setSelectedItem(nextItem);

            scrollToItem(nextItem.id);
            activatePickToLight(nextItem.id);
        }
    };

    const openQtyEdit = (item: OutboundOrderItem) => {
        setSelectedItem(item);
        setQtyInput(item.pickedQty.toString());
        setShowQtyModal(true);
    };

    const confirmManualQty = async () => {
        if (!selectedOrder || !selectedItem) return;

        const qty = parseInt(qtyInput) || 0;
        const remainingQty = selectedItem.requiredQty - selectedItem.shippedQty;

        const newOutboundOrder = await setArticleAmount({
            articleId: selectedItem.articleNumber,
            value: qty
        });
        
        
        // const updatedItems = selectedOrder.items.map(item =>
        //     item.id === selectedItem.id
        //         ? { ...item, pickedQty: qty, status: qty >= remainingQty ? 'completed' as const : 'picking' as const }
        //         : item
        // );

        // setSelectedOrder({ ...selectedOrder, items: updatedItems });

        if(newOutboundOrder){
            setSelectedOrder(newOutboundOrder);
        }


        setShowQtyModal(false);
        setSelectedItem(null);
        setQtyInput('');
    };

    const openBatchQtyEdit = (item: OutboundOrderItem, batchIndex: number) => {
        setSelectedItem(item);
        setSelectedBatchIndex(batchIndex);
        const batch = item.selectedBatches?.[batchIndex];
        setBatchQtyEditInput(batch?.quantity.toString() || '0');
        setShowBatchQtyEditModal(true);
    };

    const confirmBatchQtyEdit = async () => {
        if (!selectedOrder || !selectedItem || selectedBatchIndex === null) return;

        const newQty = parseInt(batchQtyEditInput) || 0;
        const updatedBatches = [...(selectedItem.selectedBatches || [])];

        // updatedBatches[selectedBatchIndex] = {
        //     ...updatedBatches[selectedBatchIndex],
        //     quantity: newQty
        // };

        // const totalQty = updatedBatches.reduce((sum, sb) => sum + sb.quantity, 0);

        // const updatedItems = selectedOrder.items.map(item =>
        //     item.id === selectedItem.id
        //         ? { ...item, selectedBatches: updatedBatches, pickedQty: totalQty }
        //         : item
        // );

        const batchNumber = updatedBatches[selectedBatchIndex].batch.batchNumber;

        const newOutboundOrder = await setArticleAmount({
            articleId: selectedItem.articleNumber,
            value: newQty,
            batch: batchNumber
        });
        
        if(newOutboundOrder){
            setSelectedOrder(newOutboundOrder);
        }

        // setSelectedOrder({ ...selectedOrder, items: updatedItems });

        setShowBatchQtyEditModal(false);
        setSelectedItem(null);
        setSelectedBatchIndex(null);
        setBatchQtyEditInput('');
    };

    // Order Management
    const completeOrder = async () => {

        const warehouseApi:WarehouseApi = new WarehouseApi(getWarehouseApiConfig());


        const orderId = selectedOrder?.id;

        if(orderId){

            await warehouseApi.createOutboundOrder({
                outboundOrder: selectedOrder
            })

            // await warehouseApi.confirmOutboundOrder({
            //     orderId: orderId
            // });

        }

        
        // const updatedOrders = orders.map(o =>
        //     o.id === selectedOrder?.id ? { ...o, status: 'completed' as const } : o
        // );



        // setOrders(updatedOrders);
        setSelectedOrder(null);
    };

    const startOrderScanning = () => {
        setIsScanning(true);
        setScanType('order');
    };

    const startItemScanning = () => {
        setIsScanning(true);
        setScanType('item');
    };

    const closeScanner = () => {
        setIsScanning(false);
    };


    
    const incrementArticleAmount = async (data:IArticleAmount) => {

        let orderId = selectedOrder?.id;

        if(orderId){
            const warehouseApi:WarehouseApi = new WarehouseApi(getWarehouseApiConfig());
    
            let outboundOrder:OutboundOrder|null = null;
                
            try {
                // const newSalesWrap = await salesOrderApi.incrementArticleAmount({id:orderId, });
                const tmpSalesWrap = await warehouseApi.incrementArticleAmount({orderId:orderId, itemId:data.articleId, amount:data.value.toString(), batch: data.batch});
    
                outboundOrder = tmpSalesWrap;
                
                
            } catch (error) {
                
            }    


            if(outboundOrder){
                return outboundOrder;
                // setSelectedOrder(outboundOrder);
            }

        }
        
        return null;
    }

    const setArticleAmount = async (data:IArticleAmount) => {

        let orderId = selectedOrder?.id;

        if(orderId){
            const warehouseApi:WarehouseApi = new WarehouseApi(getWarehouseApiConfig());
    
            let outboundOrder:OutboundOrder|null = null;
                
            try {
                // const newSalesWrap = await salesOrderApi.incrementArticleAmount({id:orderId, });
                const tmpSalesWrap = await warehouseApi.setArticleAmount({orderId:orderId, itemId:data.articleId, amount:data.value.toString(), batch: data.batch});
    
                outboundOrder = tmpSalesWrap;
                
                
            } catch (error) {
                
            }    


            if(outboundOrder){
                return outboundOrder;
                // setSelectedOrder(outboundOrder);
            }

        }
        
        return null;
    }

    const addNewSerialOrBatch = async (data:{articleId:string, serial?:string, batch?:string}) => {

        let orderId = selectedOrder?.id;

        if(orderId){
            const warehouseApi:WarehouseApi = new WarehouseApi(getWarehouseApiConfig());
    
            let outboundOrder:OutboundOrder|null = null;
                
            try {
                // const newSalesWrap = await salesOrderApi.incrementArticleAmount({id:orderId, });

                                
                // Body nach dem Schema des OpenAPI-Generators
                const body: AddItemToOutboundOrderRequest = {
                    articleId: data.articleId,
                    serialNumber: data.serial,
                    batch: data.batch,
                };

                const tmpSalesWrap = await warehouseApi.addItemToOutboundOrder({orderId:orderId, addItemToOutboundOrderRequest:body});
    
                outboundOrder = tmpSalesWrap;
                
                
            } catch (error) {
                
            }    

            if(outboundOrder){
                return outboundOrder;
                // setSelectedOrder(outboundOrder);
            }

        }



    }

    const checkScan = useCallback(async (scanned: string) => {
        // hier ID aus Scan ermitteln

        return scanned;

        // return null;
    }, []);

    const runOutboundSearch = useCallback(async (id: string) => {
        let orderId = selectedOrder?.id;

        if (orderId) {
            const warehouseApi: WarehouseApi = new WarehouseApi(getWarehouseApiConfig());

            let outboundOrder: OutboundOrder | null = null;

            try {
                // const newSalesWrap = await salesOrderApi.incrementArticleAmount({id:orderId, });
                const tmpSalesWrap = await warehouseApi.getOutboundOrderScan({ orderId: orderId, scanned: id });

                outboundOrder = tmpSalesWrap;


            } catch (error) {

            }


            if (outboundOrder) {
                return outboundOrder;
                // setSelectedOrder(outboundOrder);
            }


        }



        return false;
    }, [selectedOrder?.id]);



    return {
        // State
        orders,
        selectedOrder,
        isScanning,
        scanType,
        currentView,
        showBatchModal,
        showSerialModal,
        showQtyModal,
        showBatchQtyEditModal,
        selectedItem,
        selectedBatchIndex,
        serialInput,
        qtyInput,
        batchQtyInput,
        batchQtyEditInput,
        selectedBatchesTemp,
        expandedItems,
        expandedLocations,
        itemRefs,

        // Calculated values
        totalRequiredQty,
        totalPickedQty,
        totalShippedQty,
        progress,
        allCompleted,
        hasPickedItems,

        // State setters
        setCurrentView,
        setSerialInput,
        setQtyInput,
        setBatchQtyInput,
        setBatchQtyEditInput,
        setShowBatchModal,
        setShowSerialModal,
        setShowQtyModal,
        setShowBatchQtyEditModal,
        setSelectedItem,
        setSelectedBatchIndex,

        // Actions
        handleOrderSelect,
        handleScan,
        handleItemClick,
        toggleItemExpanded,
        toggleLocationExpanded,
        openBatchSelection,
        addBatchToSelection,
        removeBatchFromSelection,
        getTotalSelectedQty,
        confirmBatchSelection,
        openSerialInput,
        addSerialNumber,
        removeSerialNumber,
        confirmPartialSerials,
        confirmStandardPick,
        openQtyEdit,
        confirmManualQty,
        openBatchQtyEdit,
        confirmBatchQtyEdit,
        completeOrder,
        startOrderScanning,
        startItemScanning,
        closeScanner,
        
    };
};